import React, { useMemo, useState } from 'react';
import { mockExperiences } from '../data/mockExperiences';
import { ExperienceRecord, IndividualExperience, VerificationStatus } from '../types/experience';
import { RecordDrawer } from './RecordDrawer';
import { Badge, StatusBadge } from './ui/Badge';
import { GuarantorReview } from './GuarantorReview';
import { ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, LayoutGridIcon, TableIcon, MoreHorizontalIcon } from 'lucide-react';
type SortKey = keyof IndividualExperience | 'Guarantor__c' | 'Net_Profit__c';
type SortDirection = 'asc' | 'desc';
interface FlattenedRecord {
  experience: IndividualExperience;
  guarantor: ExperienceRecord;
}
export function TriageTable() {
  const [data, setData] = useState<ExperienceRecord[]>(mockExperiences);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'review'>('table');
  const [pickedGuarantorId, setPickedGuarantorId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  }>({
    key: 'Guarantor__c',
    direction: 'asc'
  });
  const [selectedGuarantor, setSelectedGuarantor] = useState<string>('all');
  // Flatten nested data structure for table view
  const flattenedData = useMemo(() => {
    const flat: FlattenedRecord[] = [];
    data.forEach(guarantorRecord => {
      if (guarantorRecord.individualExperiences) {
        guarantorRecord.individualExperiences.forEach(exp => {
          flat.push({
            experience: exp,
            guarantor: guarantorRecord
          });
        });
      }
    });
    return flat;
  }, [data]);
  // Derive unique guarantor names from data
  const guarantors = useMemo(() => {
    const unique = Array.from(new Set(data.map(d => d.Guarantor__c).filter(Boolean)));
    return unique.sort() as string[];
  }, [data]);
  // Filter and Sort
  const processedData = useMemo(() => {
    let result = [...flattenedData];
    // Filter by selected guarantor tab
    if (selectedGuarantor !== 'all') {
      result = result.filter(item => item.guarantor.Guarantor__c === selectedGuarantor);
    }
    // Sort
    result.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      if (sortConfig.key === 'Guarantor__c') {
        aValue = a.guarantor.Guarantor__c;
        bValue = b.guarantor.Guarantor__c;
      } else {
        aValue = a.experience[sortConfig.key as keyof IndividualExperience];
        bValue = b.experience[sortConfig.key as keyof IndividualExperience];
      }
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [flattenedData, sortConfig, selectedGuarantor]);
  // Filter guarantors for review mode based on selection
  const reviewData = useMemo(() => {
    if (selectedGuarantor === 'all') return data;
    return data.filter(g => g.Guarantor__c === selectedGuarantor);
  }, [data, selectedGuarantor]);
  const handleSort = (key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  const handleUpdateStatus = (id: string, status: VerificationStatus) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => exp.Id === id ? {
        ...exp,
        triageStatus: status
      } : exp) || []
    })));
    setSelectedRecordId(null);
  };
  const handleUpdateNotes = (id: string, notes: string) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => exp.Id === id ? {
        ...exp,
        triageNotes: notes
      } : exp) || []
    })));
  };
  const selectedItem = flattenedData.find(item => item.experience.Id === selectedRecordId);
  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const getRowClass = (status?: VerificationStatus) => {
    switch (status) {
      case 'verified':
        return 'bg-green-50/50 hover:bg-green-100/50';
      case 'flagged':
        return 'bg-red-50/50 hover:bg-red-100/50';
      case 'skipped':
        return 'bg-gray-50/50 hover:bg-gray-100/50 opacity-60';
      default:
        return 'hover:bg-blue-50/50';
    }
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: SortKey;
  }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDownIcon className="w-3 h-3 ml-1 text-gray-300" data-id="element-112" />;
    return sortConfig.direction === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1 text-blue-600" data-id="element-113" /> : <ArrowDownIcon className="w-3 h-3 ml-1 text-blue-600" data-id="element-114" />;
  };
  return <div className="flex flex-col h-screen bg-gray-50 overflow-hidden font-sans" data-id="element-115">
      {/* Top Bar */}
      <div className="h-14 bg-gray-900 flex items-center px-5 flex-shrink-0 z-30 space-x-4" data-id="element-116">
        
        <div className="h-5 w-px bg-gray-700 mx-1" data-id="element-117"></div>
        <button onClick={() => setViewMode('table')} className={`flex items-center space-x-2 px-4 py-2 rounded transition-all text-sm font-semibold ${viewMode === 'table' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`} title="Table View" data-id="element-118">
          <TableIcon className="w-4 h-4" data-id="element-119" />
          <span data-id="element-120">Triage</span>
        </button>
        <button onClick={() => setViewMode('review')} className={`flex items-center space-x-2 px-4 py-2 rounded transition-all text-sm font-semibold ${viewMode === 'review' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`} title="Review Mode" data-id="element-121">
          <LayoutGridIcon className="w-4 h-4" data-id="element-122" />
          <span data-id="element-123">Review</span>
        </button>
      </div>

      <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden" data-id="element-124">
        {/* Guarantor Filter Bar */}
        <div className="h-14 bg-white border-b border-gray-200 px-5 flex items-center justify-between flex-shrink-0 z-20" data-id="element-125">
          <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar" data-id="element-126">
            <button onClick={() => setSelectedGuarantor('all')} className={`px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${selectedGuarantor === 'all' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-127">
              All
            </button>
            {guarantors.map(g => <button key={g} onClick={() => setSelectedGuarantor(g)} className={`px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${selectedGuarantor === g ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-128">
                {g}
              </button>)}
          </div>

          <div className="flex items-center text-sm text-gray-500 ml-4 flex-shrink-0" data-id="element-129">
            <span className="font-bold text-gray-900 mr-1" data-id="element-130">
              {viewMode === 'table' ? processedData.length : reviewData.length}
            </span>{' '}
            {viewMode === 'table' ? 'records' : 'guarantors'}
          </div>
        </div>

        {/* Content Area */}
        {viewMode === 'review' ? <GuarantorReview data={reviewData} pickedGuarantorId={pickedGuarantorId} onPickGuarantor={setPickedGuarantorId} data-id="element-131" /> : <div className="flex-1 overflow-auto bg-white" data-id="element-132">
            <table className="min-w-full divide-y divide-gray-200 border-collapse" data-id="element-133">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm" data-id="element-134">
                <tr data-id="element-135">
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Experience_Type__c')} data-id="element-136">
                    <div className="flex items-center" data-id="element-137">
                      Type <SortIcon columnKey="Experience_Type__c" data-id="element-138" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-139">
                    Property Address
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Guarantor__c')} data-id="element-140">
                    <div className="flex items-center" data-id="element-141">
                      Guarantor <SortIcon columnKey="Guarantor__c" data-id="element-142" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Purchase_Price__c')} data-id="element-143">
                    <div className="flex items-center justify-end" data-id="element-144">
                      Purchase <SortIcon columnKey="Purchase_Price__c" data-id="element-145" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Sale_Price__c')} data-id="element-146">
                    <div className="flex items-center justify-end" data-id="element-147">
                      Sale <SortIcon columnKey="Sale_Price__c" data-id="element-148" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Rehab_Amount__c')} data-id="element-149">
                    <div className="flex items-center justify-end" data-id="element-150">
                      Rehab <SortIcon columnKey="Rehab_Amount__c" data-id="element-151" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Net_Profit__c')} data-id="element-152">
                    <div className="flex items-center justify-end" data-id="element-153">
                      Net Profit <SortIcon columnKey="Net_Profit__c" data-id="element-154" />
                    </div>
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-155">
                    Status
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-156">
                    Verified
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-157">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100" data-id="element-158">
                {processedData.map(({
              experience,
              guarantor
            }, idx) => <tr key={experience.Id} onClick={() => setSelectedRecordId(experience.Id)} className={`cursor-pointer transition-colors ${getRowClass(experience.triageStatus)}`} data-id="element-159">
                    <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-160">
                      <Badge variant="outline" data-id="element-161">
                        {experience.Experience_Type__c}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5" data-id="element-162">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-163">
                        {experience.Property_Address__c.street}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5" data-id="element-164">
                        {experience.Property_Address__c.city},{' '}
                        {experience.Property_Address__c.state}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-165">
                      <div className="text-sm font-medium text-gray-900" data-id="element-166">
                        {guarantor.Guarantor__c}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-right text-sm tabular-nums text-gray-600" data-id="element-167">
                      {formatCurrency(experience.Purchase_Price__c)}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-right text-sm tabular-nums text-gray-600" data-id="element-168">
                      {formatCurrency(experience.Sale_Price__c)}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-right text-sm tabular-nums text-gray-600" data-id="element-169">
                      {formatCurrency(experience.Rehab_Amount__c)}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-right tabular-nums" data-id="element-170">
                      {experience.Net_Profit__c !== null ? <span className={`text-sm font-semibold ${experience.Net_Profit__c >= 0 ? 'text-green-700' : 'text-red-700'}`} data-id="element-171">
                          {experience.Net_Profit__c >= 0 ? '+' : ''}
                          {formatCurrency(experience.Net_Profit__c)}
                        </span> : <span className="text-sm text-gray-400" data-id="element-172">—</span>}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-center" data-id="element-173">
                      <StatusBadge status={experience.triageStatus || 'pending'} data-id="element-174" />
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-center" data-id="element-175">
                      {experience.Verified_Flag__c ? <CheckCircleIcon className="w-4 h-4 text-green-500 mx-auto" data-id="element-176" /> : <span className="text-gray-300 mx-auto block text-center" data-id="element-177">
                          —
                        </span>}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-center" data-id="element-178">
                      <button className="text-gray-400 hover:text-gray-600" data-id="element-179">
                        <MoreHorizontalIcon className="w-4 h-4" data-id="element-180" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>

            {processedData.length === 0 && <div className="p-16 text-center text-gray-400 text-sm" data-id="element-181">
                No records found for this guarantor.
              </div>}
          </div>}

        {/* Detail Drawer */}
        <RecordDrawer isOpen={!!selectedRecordId} onClose={() => setSelectedRecordId(null)} record={selectedItem?.experience || null} guarantor={selectedItem?.guarantor || null} onVerify={id => handleUpdateStatus(id, 'verified')} onFlag={id => handleUpdateStatus(id, 'flagged')} onSkip={id => handleUpdateStatus(id, 'skipped')} onUpdateNotes={handleUpdateNotes} data-id="element-182" />
      </div>
    </div>;
}